/**
 * The four reads behind `/rezerwacje`'s calendar, as `queryOptions`.
 *
 * - the trainer's own schedule (`GET /calendarevent/schedule?role=trainer`) over the shown range,
 *   UTC-marked and a day wider each way by `entities/calendar-event`'s `scheduleWindow` — the same
 *   window rule the client calendar reads with, because the endpoint answers 400 without the marker;
 * - the working hours, overrides and blocks (`GET /trainer-availability`), which also carry the zone
 *   the whole grid is drawn in;
 * - the booking policy, for one question: do clients book themselves at all;
 * - the trainer's own free starts (`GET /trainer-availability/slots`), asked only when they do.
 *
 * `includeRemote` is not passed, and it would change nothing if it were: for the trainer's role the
 * server answers every row that names the trainer, remote or not, and the flag is inert there.
 */

import { queryOptions, type QueryClient } from '@tanstack/react-query';

import { scheduleWindow } from '~/entities/calendar-event';
import { BOOKING_ROOT } from '~/features/credit-ledger';
import { getCalendareventSchedule } from '~/shared/api/generated/endpoints/calendar-event/calendar-event';
import { getTrainerAvailability } from '~/shared/api/generated/endpoints/trainer-availability/trainer-availability';
import { getTrainerAvailabilitySlots } from '~/shared/api/generated/endpoints/trainer-slots/trainer-slots';
import type {
  TrainWithMeModulesBookingDtosResponsesTrainerAvailabilityResponse as TrainerAvailability,
  TrainWithMeModulesBookingDtosResponsesWeeklyWindowResponse as WeeklyWindow,
} from '~/shared/api/generated/model';

import type { VisibleRange } from '../model/calendarRange';

const ROOT = BOOKING_ROOT;

/**
 * A saved week, drawn at once: `PUT /trainer-availability/weekly` answers with nothing, so every
 * cached hours read takes the week just sent until the read after the write lands. Without it the
 * card would show the old week between the save's answer and that read's.
 */
export function weekSaved(queryClient: QueryClient, weekly: readonly WeeklyWindow[]) {
  queryClient.setQueriesData<TrainerAvailability>({ queryKey: [...ROOT, 'availability'] }, (old) =>
    old === undefined ? old : { ...old, weekly: [...weekly] },
  );
}

/**
 * An exception or a block undone, drawn at once: the DELETE answers with nothing, so the cached
 * hours drop the line before the read after the write lands — the question closes on a list that
 * no longer holds it, and focus can move to the line after it.
 */
export function deviationRemoved(
  queryClient: QueryClient,
  removed: { readonly date: string } | { readonly blockId: number },
) {
  queryClient.setQueriesData<TrainerAvailability>(
    { queryKey: [...ROOT, 'availability'] },
    (old) => {
      if (old === undefined) return old;
      return 'date' in removed
        ? { ...old, overrides: old.overrides?.filter((entry) => entry.date !== removed.date) }
        : { ...old, blocks: old.blocks?.filter((entry) => entry.id !== removed.blockId) };
    },
  );
}

/**
 * Every `PATCH /booking-policy` on the screen — Ustawienia's rows and Rozliczenia's credits switch —
 * is ONE kind of write: one key to count them by, and one scope, which query-core's
 * `MutationCache` runs one at a time in the order made. A re-read after one of them waits until
 * none is still out (`policyWritesSettled`), so it never lands on a change that has not answered.
 */
export const POLICY_WRITE = {
  mutationKey: [...ROOT, 'policy', 'change'] as const,
  scope: { id: 'booking-policy' },
} as const;

/** Whether this settling write is the last policy write out — the moment to read everything again. */
export function policyWritesSettled(queryClient: QueryClient): boolean {
  return queryClient.isMutating({ mutationKey: POLICY_WRITE.mutationKey }) <= 1;
}

/** The role token the schedule endpoint reads the trainer's own view from. */
const TRAINER_ROLE = 'trainer';

/** A minute: paging back to a week just seen answers from the cache rather than drawing bars again. */
const STALE_MS = 60_000;

export function trainerScheduleQueryOptions(range: VisibleRange) {
  const window = scheduleWindow(range.first, range.last);
  const params = { from: window.from, to: window.to, role: TRAINER_ROLE };
  return queryOptions({
    queryKey: [...ROOT, 'schedule', params] as const,
    queryFn: ({ signal }) => getCalendareventSchedule(params, undefined, signal),
    staleTime: STALE_MS,
  });
}

export function trainerAvailabilityQueryOptions(range: VisibleRange) {
  const params = { from: range.first, to: range.last };
  return queryOptions({
    queryKey: [...ROOT, 'availability', params] as const,
    queryFn: ({ signal }) => getTrainerAvailability(params, undefined, signal),
    staleTime: STALE_MS,
  });
}

export function trainerSlotsQueryOptions(range: VisibleRange, enabled: boolean) {
  const params = { type: 'Training', from: range.first, to: range.last } as const;
  return queryOptions({
    queryKey: [...ROOT, 'slots', params] as const,
    queryFn: ({ signal }) => getTrainerAvailabilitySlots(params, undefined, signal),
    staleTime: STALE_MS,
    enabled,
  });
}
